/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import {
    Assignment as AssignmentIcon,
    Verified as VerifiedIcon,
    CheckCircle as CheckCircleIcon,
    HowToReg as HowToRegIcon,
} from '@mui/icons-material';
import { prepareTeachrForUpdate, updateTeachrData } from 'src/Utils/Function';
import TeachrRepository from 'src/Repositories/TeachrRepository';
import ValidateSiret from 'src/Validation/Siret';
import { userSetInfoSiret } from 'src/Redux/Actions/UserActions';
import { useProfilContext } from 'src/Contexts/ProfilContexte';
import useTailwindBrand from 'src/Theme/useTailwindBrand';
import Settings from 'src/Components/Settings';
import ButtonField from 'src/Components/Ui/ButtonField';
import FormInput from 'src/Components/Ui/FormInput';

interface SapAeProcurationFormProps {
    onNext: () => void;
}

const SapAeProcurationForm: React.FC<SapAeProcurationFormProps> = ({ onNext }) => {
    const { teachr } = useProfilContext();
    const dispatch = useDispatch();
    const { brandConfig } = useTailwindBrand();
    const primaryHex = brandConfig.colors?.primary ?? '#1976d2';

    const [siret, setSiret] = useState(teachr?.RCS || '');
    const [errorSiret, setErrorSiret] = useState(false);
    const [siretApiError, setSiretApiError] = useState<string | undefined>(undefined);
    const [siretVerified, setSiretVerified] = useState(teachr?.RCS ? true : false);
    const [siretVerifying, setSiretVerifying] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleVerifySiret = async () => {
        const validation = ValidateSiret(siret);
        if (!validation.valid) {
            setErrorSiret(true);
            return;
        }
        setSiretVerifying(true);
        setErrorSiret(false);
        try {
            const response = await TeachrRepository.getInfoBySiret(siret);
            if (response) {
                dispatch(userSetInfoSiret(response));
                if (response.header.statut === 200) {
                    setSiretVerified(true);
                    setSiretApiError(undefined);
                } else {
                    setSiretVerified(false);
                    setSiretApiError(`${response.header.message}, veuillez renseigner le bon siret`);
                }
            }
        } catch {
            setSiretVerified(false);
            setSiretApiError('Une erreur est survenue lors de la vérification.');
        } finally {
            setSiretVerifying(false);
        }
    };

    const handleContinue = async () => {
        if (!siretVerified || !teachr) return;
        setLoading(true);
        try {
            const updatedTeachr = prepareTeachrForUpdate(teachr, { RCS: siret });
            await updateTeachrData(updatedTeachr, dispatch);
            onNext();
        } catch (err) {
            console.warn('Erreur lors de l\'enregistrement du SIRET:', err);
        } finally {
            setLoading(false);
        }
    };

    const sectionBoxSx = {
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        bgcolor: 'grey.50',
    };

    return (
        <Settings
            title="Procuration AE + SAP"
            subtitle="Renseignez et vérifiez votre numéro SIRET pour établir votre procuration"
            icon={<AssignmentIcon sx={{ color: primaryHex, fontSize: 28 }} />}
            height="100%"
        >
            {/* Section SIRET */}
            <Grid item xs={12}>
                <Box sx={sectionBoxSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                bgcolor: siretVerified ? '#dcfce7' : `${primaryHex}1a`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'background-color 0.3s',
                            }}
                        >
                            <VerifiedIcon sx={{ color: siretVerified ? '#16a34a' : primaryHex, fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight="700" fontFamily={brandConfig.fontFamily} color="text.primary">
                                Numéro SIRET
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontFamily={brandConfig.fontFamily}>
                                Identifiant unique à 14 chiffres de votre établissement
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <FormInput
                            label="Numéro SIRET"
                            type="text"
                            placeholder="Ex : 91234567800016"
                            value={siret}
                            error={siretApiError ?? (errorSiret ? ValidateSiret(siret).message : undefined)}
                            onChange={(e) => {
                                setSiret(e.target.value);
                                setErrorSiret(false);
                                setSiretApiError(undefined);
                                setSiretVerified(false);
                            }}
                        />

                        <button
                            onClick={handleVerifySiret}
                            disabled={siretVerifying || !siret}
                            className="w-full py-2.5 px-4 rounded-md font-semibold text-sm transition-all disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed text-white flex items-center justify-center gap-2"
                            style={siretVerifying || !siret ? undefined : { backgroundColor: siretVerified ? '#16a34a' : primaryHex }}
                        >
                            {siretVerifying ? (
                                <>
                                    <CircularProgress size={14} sx={{ color: 'white' }} />
                                    Vérification en cours...
                                </>
                            ) : siretVerified ? (
                                <>
                                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                                    SIRET vérifié
                                </>
                            ) : (
                                'Valider le SIRET'
                            )}
                        </button>
                    </Box>

                    {/* Résultat de vérification */}
                    {siretVerified && (
                        <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid #bbf7d0', bgcolor: '#f0fdf4', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 22, flexShrink: 0 }} />
                            <Typography variant="body2" fontWeight="600" sx={{ color: '#166534', fontFamily: brandConfig.fontFamily }}>
                                SIRET valide. Vous pouvez passer à l'étape suivante pour signer les conditions.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Grid>

            {/* Bouton d'action */}
            <Grid item xs={12}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flexDirection: { xs: 'column', lg: 'row' },
                        gap: 2,
                        pt: 1,
                    }}
                >
                    <ButtonField
                        label="Suivant"
                        variant="contained"
                        className="w-full font-bold lg:w-auto"
                        onClick={handleContinue}
                        startIcon={<HowToRegIcon />}
                        loading={loading}
                        disabled={!siretVerified}
                    />
                </Box>
            </Grid>
        </Settings>
    );
};

export default SapAeProcurationForm;
